/**
 * Client-side port of the form-builder backend's aiBlocks.ts + aiText.ts —
 * the AI Generator field now calls the AI service directly from the browser
 * (see services/api/fabaiApi.ts), so the prompt building and response parsing
 * live here. Behaviour is intentionally identical to the server's.
 */

export interface AiBlockInput {
  label: string;
  /** Already `{{fieldName}}`-resolved, ready to send to the AI. */
  prompt: string;
}

export interface AiBlockResult {
  label: string;
  output: string;
}

const TOKEN = /\{\{\s*([A-Za-z_][A-Za-z0-9_.-]*)\s*\}\}/g;

/**
 * Replaces `{{fieldName}}` with that field's current value from `answers` (the
 * visitor's in-progress answers across the whole form, including this same
 * field, so a block can reference the component's own main input by name).
 * A field with no answer yet resolves to an empty string.
 */
export const resolveBlockPrompt = (
  template: string,
  answers: Record<string, any>,
): string =>
  template.replace(TOKEN, (_, name) => {
    const value = answers?.[name];
    return value === undefined || value === null ? '' : String(value);
  });

// Close to the phrasing verified against the live AI service (returns a
// strict JSON array reliably); kept fixed rather than templated so behaviour
// doesn't drift between requests.
const INSTRUCTION =
  'Give output for each block with their label and prompt, with a new key ' +
  '"output" holding the result. Return a strict JSON array of the given ' +
  'objects and nothing else — no markdown formatting, no commentary.';

/** The `userInput` sent to the AI service: the blocks plus the fixed instruction. */
export const buildBlocksUserInput = (blocks: AiBlockInput[]): string =>
  `${JSON.stringify(blocks)}\n\n${INSTRUCTION}`;

/**
 * Parses the AI service's reply into `{ [label]: output }`. The model is a
 * text generator, not a JSON API, so this is defensive: it strips a code
 * fence if the model added one despite being told not to, and a block the
 * response doesn't clearly cover comes back as an empty string with a warning
 * rather than failing the whole batch.
 */
export const parseBlockOutputs = (
  raw: string,
  labels: string[],
): { outputs: Record<string, string>; warnings: string[] } => {
  const cleaned = String(raw ?? '')
    .trim()
    .replace(/^```[a-z]*\s*/i, '')
    .replace(/```\s*$/, '')
    .trim();

  let parsed: any;
  try {
    parsed = JSON.parse(cleaned);
  } catch {
    throw new Error('The AI service did not return a valid result. Please try again.');
  }
  if (!Array.isArray(parsed)) {
    throw new Error('The AI service returned an unexpected result. Please try again.');
  }

  const byLabel = new Map<string, string>();
  for (const item of parsed) {
    if (item && typeof item.label === 'string' && typeof item.output === 'string') {
      byLabel.set(item.label, item.output.trim());
    }
  }

  const outputs: Record<string, string> = {};
  const warnings: string[] = [];
  for (const label of labels) {
    const output = byLabel.get(label);
    if (output === undefined) {
      outputs[label] = '';
      warnings.push(`Could not generate "${label}". Try regenerating it.`);
    } else {
      outputs[label] = output;
    }
  }
  return { outputs, warnings };
};

/** Builds the results array in the order the blocks were requested. */
export const toBlockResults = (
  blocks: AiBlockInput[],
  outputs: Record<string, string>,
): AiBlockResult[] => blocks.map(({ label }) => ({ label, output: outputs[label] || '' }));

/**
 * Pulls the generated text out of the AI service's response. The response
 * nests it under `generation.data.response`, sometimes as a string, sometimes
 * as a list or as an object, so this tries the known shapes in order.
 */
// const TEXT_KEYS = ['text', 'content', 'post', 'body', 'message', 'response'];

const toText = (value: any, depth = 0): string => {
  if (value === null || value === undefined || depth > 4) return '';
  if (typeof value === 'string') return value.trim();
  return JSON.stringify(value, null, 2).trim() || '';
};

export const extractAiText = (data: any): string =>
  {
    return toText(data?.generation?.data?.response?.message) ||
      toText(data?.generation?.data?.response) ||
      toText(data?.generation?.data?.response) ||
      toText(data?.generation?.data) ||
      toText(data?.response) ||
      toText(data?.text);
  };
